import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { toast } from "sonner";
import { IndianRupee, Search, Save, Loader2, RefreshCw } from "lucide-react";

const STATUS_COLORS = {
  paid: "text-emerald-600", failed: "text-red-500",
  created: "text-amber-600", refunded: "text-slate-500",
};

export default function AdminPayments() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [gst, setGst] = useState(18);
  const [filters, setFilters] = useState({ status: "", payment_type: "", q: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);
  const [savingPlan, setSavingPlan] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const qs = Object.entries(filters).filter(([, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const [pay, pl] = await Promise.all([
        api.get(`/admin/payments${qs ? "?" + qs : ""}`),
        api.get("/admin/payment-plans"),
      ]);
      setStats(pay.data.stats);
      setRows(pay.data.payments || []);
      setPlans(pl.data.plans || []);
      setGst(pl.data.gst_percent ?? 18);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load payments"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  async function savePlan(p) {
    setSavingPlan(p.plan_id);
    try {
      await api.put(`/admin/payment-plans/${p.plan_id}`, {
        monthly_price: p.monthly_price, yearly_price: p.yearly_price,
        listings: p.listings, active: p.active, name: p.name, description: p.description,
      });
      toast.success(`${p.name} updated`);
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setSavingPlan(null);
    }
  }

  async function saveGst() {
    try {
      await api.put("/admin/payment-settings", { gst_percent: Number(gst) });
      toast.success("GST updated");
    } catch (e) {
      toast.error(errMsg(e, "GST update failed"));
    }
  }

  const statCards = stats ? [
    ["Total Revenue", `₹${stats.total_revenue.toLocaleString("en-IN")}`],
    ["Monthly Revenue", `₹${stats.monthly_revenue.toLocaleString("en-IN")}`],
    ["Successful", stats.successful],
    ["Failed", stats.failed],
    ["Pending", stats.pending],
    ["Active Subs", stats.active_subscriptions],
    ["Cancelled Subs", stats.cancelled_subscriptions],
    ["All Payments", stats.payment_count],
  ] : [];

  return (
    <AppShell role="admin">
      <div className="space-y-8" data-testid="admin-payments-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#282C3F]">Payments</h1>
            <p className="text-sm text-[#535665]">Razorpay revenue, transactions &amp; plan management</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm border border-[#EDEDF1] rounded-md text-[#535665]">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="payment-stats">
          {statCards.map(([label, val]) => (
            <div key={label} className="bg-white border border-[#EDEDF1] rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-widest text-[#94969F]">{label}</div>
              <div className="text-xl font-extrabold text-[#282C3F]">{val}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#EDEDF1] rounded-xl p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] uppercase text-[#94969F]">Search (name / email / order / payment ID)</label>
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full border border-[#EDEDF1] rounded-md px-3 py-2 text-sm" data-testid="payment-search" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-[#94969F]">Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border border-[#EDEDF1] rounded-md px-3 py-2 text-sm block" data-testid="payment-status-filter">
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="created">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-[#94969F]">Type</label>
            <select value={filters.payment_type} onChange={(e) => setFilters({ ...filters, payment_type: e.target.value })}
              className="border border-[#EDEDF1] rounded-md px-3 py-2 text-sm block">
              <option value="">All</option>
              <option value="subscription">Subscription</option>
              <option value="one_time">One-time</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-[#94969F]">From</label>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="border border-[#EDEDF1] rounded-md px-3 py-2 text-sm block" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-[#94969F]">To</label>
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="border border-[#EDEDF1] rounded-md px-3 py-2 text-sm block" />
          </div>
          <button onClick={load} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF3F6C] text-white text-sm"
            data-testid="apply-filters-btn">
            <Search size={14} /> Apply
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#EDEDF1] rounded-xl overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#94969F]">Loading…</div>
          ) : (
            <table className="w-full text-sm min-w-[900px]" data-testid="admin-payments-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[#94969F] border-b border-[#EDEDF1]">
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Plan/Service</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Payment ID</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[#94969F]">No payments found</td></tr>
                )}
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-[#F5F5F8]">
                    <td className="px-4 py-2 text-[#282C3F]">{p.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-[#535665]">{p.email}</td>
                    <td className="px-3 py-2">{p.label || p.service_name || "—"}</td>
                    <td className="px-3 py-2 font-semibold">₹{(p.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-xs text-[#94969F]">{p.razorpay_payment_id || "—"}</td>
                    <td className={`px-3 py-2 capitalize font-semibold ${STATUS_COLORS[p.status] || ""}`}>{p.status}</td>
                    <td className="px-3 py-2 capitalize">{p.payment_method || "—"}</td>
                    <td className="px-3 py-2 text-[#535665]">{(p.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Plan management */}
        <div className="bg-white border border-[#EDEDF1] rounded-xl p-5 space-y-4" data-testid="plan-management">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-[#282C3F]">Plan Management</h2>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#94969F]">GST %</span>
              <input type="number" value={gst} onChange={(e) => setGst(e.target.value)}
                className="w-20 border border-[#EDEDF1] rounded-md px-2 py-1 text-sm" data-testid="gst-input" />
              <button onClick={saveGst} className="px-3 py-1.5 rounded-md bg-[#282C3F] text-white text-xs">Save GST</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map((p, i) => (
              <div key={p.plan_id} className="border border-[#EDEDF1] rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-[#282C3F]">{p.name}</div>
                  <label className="flex items-center gap-1 text-xs text-[#535665]">
                    <input type="checkbox" checked={!!p.active}
                      onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, active: e.target.checked } : x))} />
                    Active
                  </label>
                </div>
                <label className="block text-[11px] text-[#94969F]">Monthly ₹
                  <input type="number" value={p.monthly_price}
                    onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, monthly_price: e.target.value } : x))}
                    className="w-full border border-[#EDEDF1] rounded-md px-2 py-1 text-sm" data-testid={`plan-monthly-${p.plan_id}`} />
                </label>
                <label className="block text-[11px] text-[#94969F]">Yearly ₹
                  <input type="number" value={p.yearly_price}
                    onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, yearly_price: e.target.value } : x))}
                    className="w-full border border-[#EDEDF1] rounded-md px-2 py-1 text-sm" />
                </label>
                <label className="block text-[11px] text-[#94969F]">Listings / month
                  <input type="number" value={p.listings}
                    onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, listings: e.target.value } : x))}
                    className="w-full border border-[#EDEDF1] rounded-md px-2 py-1 text-sm" />
                </label>
                <button onClick={() => savePlan(p)} disabled={savingPlan === p.plan_id}
                  className="w-full flex items-center justify-center gap-2 py-1.5 rounded-md bg-[#FF3F6C] text-white text-xs font-semibold"
                  data-testid={`save-plan-${p.plan_id}`}>
                  {savingPlan === p.plan_id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Save
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
